import { DEFAULT_PAGE_SIZE, FORBIDDEN_ERROR } from 'constants/config';
import { Spin, Table, Select, Pagination, TablePaginationConfig, Button, Menu, Alert } from 'antd';
import { Link } from 'react-router-dom';
import { FilterValue } from 'antd/lib/table/interface';
import withPermission from 'components/HOCs/withPermission';
import { Dispatch, SetStateAction } from 'react';
import { GetUserRequest, UserType, PaginatedUsers } from 'services/users';
// Icon import
import { ReactComponent as AddUser } from 'resources/icons/person-plus-fill.svg';
import { ReactComponent as EditIcon } from 'resources/icons/Group 524.svg';
import { ReactComponent as DeactivateIcon } from 'resources/icons/person-x-fill.svg';
import { ReactComponent as ManageRole } from 'resources/icons/users-gear.svg';
import { FaUserCheck } from 'react-icons/fa';
import UserTableColumns from './UserTableColumns';

// USER LIST COMPONENT
const { Option } = Select;
/* eslint-disable  @typescript-eslint/no-explicit-any */
type HandleTableChangeType = (
  pagination: TablePaginationConfig,
  tableFilters: Record<string, FilterValue | null>,
  sorter: any,
) => void;
type HandlePageChangeType = (page: number, pageSize: number) => void;
type OpenUpdateUserStatusModalType = (id: string, name: string, status: boolean) => void;

type UserTableProps = {
  // native to component props
  data: PaginatedUsers | undefined;
  filters: GetUserRequest;
  isLoading: boolean;
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  handleTableChange: HandleTableChangeType;
  handlePageChange: HandlePageChangeType;
  openUpdateUserStatusModal: OpenUpdateUserStatusModalType;
  setFilters: Dispatch<SetStateAction<GetUserRequest>>;
  // permission relevant props
  disabled: boolean;
  editPermission: boolean;
  setEditPermission: Dispatch<boolean>;
  changeStatusPermission: boolean;
  setChangeStatusPermission: Dispatch<boolean>;
};

const UserTable = (props: UserTableProps) => {
  const {
    data,
    filters,
    isLoading,
    handleTableChange,
    handlePageChange,
    openUpdateUserStatusModal,
    setFilters,
    disabled,
    editPermission,
    setEditPermission,
    changeStatusPermission,
    setChangeStatusPermission,
  } = props;

  return (
    <Spin spinning={isLoading}>
      {disabled ? (
        <Alert message={FORBIDDEN_ERROR} type="error" />
      ) : (
        <div>
          <div>
            <Table<UserType>
              rowKey={record => `user-${record.id}`}
              rowClassName={record => (record.isActive ? '' : 'rowInactiveColor')}
              columns={UserTableColumns({
                openUpdateUserStatusModal,
                editPermission,
                setEditPermission,
                changeStatusPermission,
                setChangeStatusPermission,
              })}
              pagination={false}
              dataSource={data?.data || []}
              size="small"
              onChange={handleTableChange}
              showSorterTooltip={false}
            />
          </div>
          <div className="pagination">
            <div className="cutomPaginationStyling">
              Showing
              <Select
                className="customPageSelector"
                defaultValue="10/page"
                onChange={e => {
                  const total = data?.total || 0;
                  const LastPage = Math.ceil(total / +e);
                  const currentPage = data?.pageNo || 1;
                  setFilters({
                    ...filters,
                    limit: +e,
                    pageNo: currentPage > LastPage ? LastPage : currentPage,
                  });
                }}>
                <Option value="10">10/page</Option>
                <Option value="20">20/page</Option>
                <Option value="30">30/page</Option>
              </Select>
              of {data?.total || 0} entries
            </div>
            <Pagination
              showLessItems
              showSizeChanger={false}
              pageSize={filters?.limit || DEFAULT_PAGE_SIZE}
              total={data?.total || 0}
              current={data?.pageNo || 1}
              onChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </Spin>
  );
};

export const UserTableWithPermission = withPermission(UserTable);

// LIST USER COMPONENT - ENDS HERE

// ADD USER COMPONENT - STARTS HERE
type LinkDisable = {
  disabled: boolean;
};

const AddUserLink = (props: LinkDisable) => {
  const { disabled } = props;
  return disabled ? (
    <>
      <Button className="tbl-top-btn" disabled>
        <AddUser className="tbl-btn-icon" /> Add User
      </Button>
    </>
  ) : (
    <Link to="/users/new-user">
      <Button className="tbl-top-btn">
        <AddUser className="tbl-btn-icon" /> Add User
      </Button>
    </Link>
  );
};

export const AddUserLinkWithPermission = withPermission(AddUserLink);

const ManageRoleLink = (props: LinkDisable) => {
  const { disabled } = props;
  return disabled ? (
    <>
      <Button className="tbl-top-btn" disabled>
        <ManageRole className="manageRole-icon tbl-btn-icon" /> Manage Roles
      </Button>
    </>
  ) : (
    <Link to="/users/manage-roles">
      <Button className="tbl-top-btn">
        <ManageRole className="manageRole-icon tbl-btn-icon" />
        Manage Roles
      </Button>
    </Link>
  );
};

export const ManageRoleLinkWithPermission = withPermission(ManageRoleLink);

// ADD USER COMPONENT - ENDS HERE

// EDIT USER COMPONENT - STARTS HERE

type EditLinkProps = {
  disabled: boolean;
  record: UserType;
  text: string;
};
const EditLink = (props: EditLinkProps) => {
  const { disabled, record, text } = props;
  if (disabled || !record?.isActive) {
    return null;
  }
  return (
    <Link to={`/users/${record.firstName} ${record.lastName}/${text}`}>
      <div className="action_DropDown">
        <EditIcon className="dropDown_icon" />
        <span className="dropDown_list_text">Edit User</span>
      </div>
    </Link>
  );
};
export const EditLinkWithPermission = withPermission(EditLink);

// EDIT USER COMPONENT - ENDS HERE

// CHANGE STATUS USER COMPONENT - STARTS HERE

type ChangeStatusLinkProps = {
  disabled: boolean;
  record: UserType;
  text: string;
  openUpdateUserStatusModal(id: string, firstName: string, status: boolean): void;
};
const ChangeStatusLink = (props: ChangeStatusLinkProps) => {
  const { disabled, record, text, openUpdateUserStatusModal } = props;

  return (
    <Menu.Item
      key={`deactivate-${record.id}`}
      className="action-menu"
      onClick={disabled ? () => {} : () => openUpdateUserStatusModal(text, record?.firstName, !record.isActive)}>
      {disabled ? null : (
        <div className="action_DropDown">
          {record.isActive ? (
            <DeactivateIcon className="dropDown_icon" />
          ) : (
            <FaUserCheck size={18} className="dropDown_icon" />
          )}
          <span className="dropDown_list_text">{record.isActive ? 'Deactivate' : 'Activate'}</span>
        </div>
      )}
    </Menu.Item>
  );
};

export const ChangeStatusLinkWithPermission = withPermission(ChangeStatusLink);

// CHANGE STATUS USER COMPONENT - ENDS HERE
